type TranslationFunction = (
  page: string,
  context: string,
  fallback?: string | null,
  variables?: Record<string, unknown>
) => string;

type LanguageFallback = {
  english: string;
  native: string;
};

const LANGUAGE_FALLBACKS = {
  en: { english: 'English', native: 'English' },
  ko: { english: 'Korean', native: '한국어' },
  zh: { english: 'Chinese', native: '中文' },
  ja: { english: 'Japanese', native: '日本語' },
  th: { english: 'Thai', native: 'ไทย' },
  vi: { english: 'Vietnamese', native: 'Tiếng Việt' },
  id: { english: 'Indonesian', native: 'Bahasa Indonesia' },
  ru: { english: 'Russian', native: 'Русский' },
  ar: { english: 'Arabic', native: 'العربية' },
  es: { english: 'Spanish', native: 'Español' },
  mn: { english: 'Mongolian', native: 'Монгол' },
};

export const SUPPORTED_LANGUAGES = [
  'en',
  'ko',
  'zh',
  'ja',
  'vi',
  'ru',
  'mn',
  'ar',
  'th',
  'id',
  'es',
];

export const SUPPORTED_LANGUAGE_CODES = Object.keys(LANGUAGE_FALLBACKS);

/**
 * Get language fallback information for a given code
 */
const getLanguageFallback = (code: string): LanguageFallback => {
  return LANGUAGE_FALLBACKS[code as keyof typeof LANGUAGE_FALLBACKS];
};

/**
 * Get the English name for a language using translation function
 */
const getEnglishLanguageName = (
  t: TranslationFunction | undefined,
  code: string,
  fallback: LanguageFallback
): string => {
  return t?.('common', `language_names.${code}`, fallback.english) ?? fallback.english;
};

/**
 * Get the native name for a language using translation function
 */
const getNativeLanguageName = (
  t: TranslationFunction | undefined,
  code: string,
  fallback: LanguageFallback,
  _englishName: string
): string => {
  return t?.('language_native_names', code, fallback.native) ?? fallback.native;
};

/**
 * Create a combined label from native and English names
 */
const createCombinedLanguageLabel = (nativeName: string, englishName: string): string => {
  return nativeName && nativeName !== englishName ? `${nativeName} (${englishName})` : englishName;
};

export const normalizeLanguageCode = (languageCode: string): string => languageCode;

export const languageToUrlCode = (languageCode: string): string => languageCode;

export const getLanguageDisplayNames = (t: TranslationFunction | undefined, code: string) => {
  const fallback = getLanguageFallback(code);
  const englishName = getEnglishLanguageName(t, code, fallback);
  const nativeName = getNativeLanguageName(t, code, fallback, englishName);
  const combinedLabel = createCombinedLanguageLabel(nativeName, englishName);

  return {
    englishName,
    nativeName,
    combinedLabel,
  };
};
